/**
 * Created by mac on 7/26/25
 */

const VolumeBalancer = function (params) {
    params = params || {};
    this.beta = params.beta != null ? params.beta : 0.95; // smoothing factor
    this.gain = params.gain != null ? params.gain : 0.5; // correction strength

    // should be saved/loaded between sessions
    this.surplusEWMA = params.surplus || 0;
    this.t = params.t || 0;
};

VolumeBalancer.prototype._expectedEnergyGeometric = function (p, stagesEnergy) {
    let e = stagesEnergy[stagesEnergy.length - 1];
    for (let k = stagesEnergy.length - 2; k >= 0; k--) {
        e = p * stagesEnergy[k] + (1 - p) * e;
    }
    return e;
};

VolumeBalancer.prototype._solvePForTarget = function (targetPerItem, stagesEnergy) {
    let lo = 0, hi = 1;
    for (let i = 0; i < 20; i++) {
        const mid = (lo + hi) / 2;
        const e = this._expectedEnergyGeometric(mid, stagesEnergy);
        if (e > targetPerItem) {
            lo = mid;
        } else {
            hi = mid;
        }
    }
    return (lo + hi) / 2;
};

VolumeBalancer.prototype.randomStage = function (line, dataSource, ek) {
    const { minStage, maxStage, stagesEnergy } = dataSource.getStagesFromLine(line);

    // console.log(line, minStage, maxStage, stagesEnergy);

    const pseudoRandom = dataSource.getPseudoRandom();

    const targetPerItem = (dataSource.getTargetVolume() - this.gain * this.surplusEWMA) / ek;
    let p = this._solvePForTarget(targetPerItem, stagesEnergy);

    const MIN_PROB = 0.05;
    if (p < 1e-9) {
        p = MIN_PROB;
    } else if (p > 1 - 1e-9) {
        p = 1 - MIN_PROB;
    }

    let k = minStage;
    for (; k < maxStage; k++) {
        if (pseudoRandom.nextDouble() <= p) {
            break;
        }
    }

    // console.log(line, k, this.surplusEWMA, targetPerItem, stagesEnergy[k - minStage]);
    // console.log(p.toFixed(3), stagesEnergy);
    return k;
};

VolumeBalancer.prototype.status = function () {
    return {
        surplus: this.surplusEWMA / (1 - Math.pow(this.beta, this.t))
    };
};

VolumeBalancer.prototype.updateAfterOrder = function (order, dataSource) {
    const orderEnergy = dataSource.getOrderVolume(order);
    this.t++;

    const orderSurplus = orderEnergy - dataSource.getTargetVolume();
    this.surplusEWMA = this.beta * this.surplusEWMA + (1 - this.beta) * orderSurplus;

    if (isNaN(this.surplusEWMA)) {
        cleverapps.throwAsync("surplus became NaN");
    }
};

const LineBalancer = function (options) {
    this.beta = options.beta;
    this.balances = options.balances || {};
    this.t = options.t || 0;
};

LineBalancer.prototype.addNewLines = function (dataSource) {
    const list = dataSource.listLinesWithTargets();

    let sum = 0;
    let count = 0;
    for (const line in list) {
        if (this.balances[line] !== undefined) {
            sum += this.balances[line];
            count++;
        }
    }

    const avg = count > 0 ? sum / count : 0;

    for (const line in list) {
        if (this.balances[line] === undefined) {
            this.balances[line] = avg;
        }
    }
};

LineBalancer.prototype._weightedChoice = function (items, weights, pseudoRandom) {
    const total = weights.reduce((a, b) => a + b, 0);
    let r = pseudoRandom.nextDouble() * total;
    for (let i = 0; i < items.length; i++) {
        if (r < weights[i]) {
            return items[i];
        }
        r -= weights[i];
    }
    return items[items.length - 1];
};

LineBalancer.prototype.calcEMAs = function () {
    const res = {};

    for (const line in this.balances) {
        res[line] = this.balances[line] / (1 - Math.pow(this.beta, this.t));
    }

    return res;
};

LineBalancer.prototype.status = function (dataSource) {
    const weights = this._calcWeights(dataSource);

    const linesWithTargets = dataSource.listLinesWithTargets();
    const lines = Object.keys(linesWithTargets);

    // normalize
    const total = weights.reduce((a, b) => a + b, 0);

    const probs = {};
    weights.forEach((item, index) => {
        probs[lines[index]] = Math.round(item / total * 100);
    });

    return probs;
};

LineBalancer.prototype._calcWeights = function (dataSource) {
    this.addNewLines(dataSource);

    const linesWithTargets = dataSource.listLinesWithTargets();
    const lines = Object.keys(linesWithTargets);

    let totalBalance = 0;
    let totalTarget = 0;
    lines.forEach((line) => {
        totalBalance += this.balances[line];
        totalTarget += linesWithTargets[line];
    });

    if (totalBalance === 0) {
        totalBalance = 1;
    }

    if (totalTarget === 0) {
        totalTarget = 1;
    }

    const diffs = lines.map((k) => {
        const diff = (linesWithTargets[k] / totalTarget) - (this.balances[k] / totalBalance);
        return Math.max(0, diff);
    });

    const totalDiffs = diffs.reduce((a, b) => a + b, 0) || 1;
    return diffs.map((diff) => diff / totalDiffs + 0.25 / lines.length);
};

LineBalancer.prototype.randomLine = function (dataSource) {
    const linesWithTargets = dataSource.listLinesWithTargets();
    const lines = Object.keys(linesWithTargets);

    const weights = this._calcWeights(dataSource);
    const pseudoRandom = dataSource.getPseudoRandom();
    return this._weightedChoice(lines, weights, pseudoRandom);
};

LineBalancer.prototype.updateAfterOrder = function (order, dataSource) {
    this.addNewLines(dataSource);

    this.t++;

    for (const line in this.balances) {
        this.balances[line] *= this.beta;
    }

    order.forEach((ingredient) => {
        const energyPerItem = dataSource.getEnergyForItem(ingredient.line, ingredient.stage);
        this.balances[ingredient.line] += (1 - this.beta) * energyPerItem;

        if (isNaN(this.balances[ingredient.line])) {
            cleverapps.throwAsync(`line ${ingredient.line} balance became NaN`);
        }
    });
};

// eslint-disable-next-line
var SimpleAlgorithm = function (generatorDataProvider) {
    this.dataSource = generatorDataProvider;

    const data = cleverapps.dataLoader.load(DataLoader.TYPES.SIMPLE_ALGORITHM) || {};

    this.lineBalancer = new LineBalancer({
        beta: 0.98,
        balances: data.balances,
        t: data.t
    });
    this.volumeBalancer = new VolumeBalancer({
        beta: 0.95,
        gain: 0.5,
        surplus: data.surplus,
        t: data.tv
    });
};

SimpleAlgorithm.prototype.getInfo = function () {
    return {
        surplus: this.volumeBalancer.surplusEWMA,
        balances: this.lineBalancer.balances,
        t: this.lineBalancer.t,
        tv: this.volumeBalancer.t
    };
};

SimpleAlgorithm.prototype.updateAfterOrder = function (order) {
    this.lineBalancer.updateAfterOrder(order, this.dataSource);
    this.volumeBalancer.updateAfterOrder(order, this.dataSource);

    cleverapps.dataLoader.save(DataLoader.TYPES.SIMPLE_ALGORITHM, this.getInfo());
};

SimpleAlgorithm.prototype.next = function () {
    this.dataSource.updatePseudoRandomSeed();
    const order = [];
    const k = this.dataSource.howManyIngredients();

    // expected items/order (from your own k logic!)
    const ek = [1.33, 2, 2.4][k - 1]; //  240/1.33 = 180    240 = 2*120   300 3*100

    const existing = typeof cc !== "undefined" ? this.dataSource.mapExistingItems() : {};

    for (let i = 0; i < k; i++) {
        const chosenLine = this.lineBalancer.randomLine(this.dataSource);
        const { minStage, maxStage } = this.dataSource.getStagesFromLine(chosenLine);

        existing[chosenLine] = existing[chosenLine] || {};
        let stage = this.volumeBalancer.randomStage(chosenLine, this.dataSource, ek);

        if (existing[chosenLine][stage]) {
            const adjustLimit = (i === 0) ? 3 : 1;
            for (let dd = 1; dd <= adjustLimit; dd++) {
                if (stage - dd >= minStage && !existing[chosenLine][stage - dd]) {
                    stage -= dd;
                    break;
                }

                if (stage + dd <= maxStage && !existing[chosenLine][stage + dd]) {
                    stage += dd;
                    break;
                }
            }
        }

        if (existing[chosenLine][stage] && !(order.length === 0 && i === k - 1)) {
            continue;
        }

        existing[chosenLine][stage] = true;
        const ingredient = { line: chosenLine, stage };

        const capLimit = this.dataSource.getCapLimit();
        if (capLimit && order.length > 1) {
            const orderValue = this.dataSource.getOrderVolume(order.concat(ingredient));
            const target = this.dataSource.getTargetVolume();
            if (orderValue > target * capLimit) {
                break;
            }
        }

        order.push(ingredient);
    }

    this.updateAfterOrder(order);

    return order;
};
